// src/Pages/VerifyCertificate.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CertificatePreview from '../Components/CertificatePreview';
import './VerifyCertificate.css';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://api.avtechbmt.cloud';
const CERTS_API = `${API_BASE}/api/certificates`;

/* Native certificate dimensions (A4 landscape @ 96dpi) */
const CERT_NATIVE_WIDTH_PX = 1123;  // 297mm ≈ 1123px
const CERT_NATIVE_HEIGHT_PX = 794;  // 210mm ≈ 794px

export default function VerifyCertificate() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  /* ------------------------------------------------------------------
     Auto-scaling refs & state for the certificate
     ------------------------------------------------------------------ */
  const scalerRef = useRef(null);
  const [certScale, setCertScale] = useState(1);

  useEffect(() => {
    const el = scalerRef.current;
    if (!el) return;

    const update = () => {
      const availableWidth = el.clientWidth;
      if (availableWidth > 0) {
        const nextScale = Math.min(1, availableWidth / CERT_NATIVE_WIDTH_PX);
        setCertScale(nextScale);
      }
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('orientationchange', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, [result]); // re-measure once result is rendered

  /* ================================================================
     FETCH / VERIFY
     ================================================================ */
  useEffect(() => {
    if (!token) {
      setError('Missing verification code.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const url = `${CERTS_API}/verify/${token}`;
        console.log('[VerifyCertificate] GET', url);

        const res = await fetch(url);
        const raw = await res.text();

        let data;
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error(
            `Server returned non-JSON (HTTP ${res.status}). Backend at ${CERTS_API}?`
          );
        }

        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Verification failed.');
        }

        setResult(data);
      } catch (err) {
        console.error('[VerifyCertificate]', err);
        if (err.message.includes('Failed to fetch')) {
          setError(
            `Could not reach the server at ${CERTS_API}. Make sure the backend is running.`
          );
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  /* ================================================================
     LOADING
     ================================================================ */
  if (loading) {
    return (
      <div className="verify-page">
        <div className="verify-shell">
          <div className="verify-loading">
            <div className="verify-spinner" />
            <h2>Verifying Certificate…</h2>
            <p>Looking up authenticity in our records.</p>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================
     ERROR
     ================================================================ */
  if (error) {
    return (
      <div className="verify-page">
        <div className="verify-shell">
          <div className="verify-card verify-error">
            <div className="verify-icon">⚠️</div>
            <h2>Verification Failed</h2>
            <p className="verify-message">{error}</p>
            <p className="verify-api-hint">API: {CERTS_API}</p>
          </div>
        </div>
      </div>
    );
  }

  /* ================================================================
     RESULT
     ================================================================ */
  const isValid = result?.valid === true;
  const d = result?.data;
  const isProfessional = d?.participantType === 'professional';

  /* --------------------------------------------------------------
     Build preview data — pass every field the certificate
     component understands, so the adaptive declaration renders
     exactly the same as it did in the admin preview.
     -------------------------------------------------------------- */
  const certForPreview = d
    ? {
        // identity
        recipientName: d.recipientName,
        participantType: d.participantType,

        // academic / professional
        universityName: d.universityName || d.organizationName || '',
        organizationName: d.organizationName || '',
        fieldName: d.fieldName,
        designation: d.designation || '',

        // program meta
        certificateType: d.certificateType || 'internship',
        title: d.title || '',
        description: d.description || '',
        companyName: d.companyName || 'AV Tech Baramati Private Limited',
        location: d.location || '',

        // dates
        startDate: d.startDate,
        endDate: d.endDate,
        duration: d.duration || '',
        issueDate: d.issueDate,

        // content
        skills: d.skills || '',
        internalNotes: d.internalNotes || '',

        // signatory
        authorizedSignatory: d.authorizedSignatory || 'Authorized Signatory',
        signatoryTitle: d.signatoryTitle || 'Director',

        // verification
        certificateId: d.certificateId,
        verificationToken: token,
        status: d.status,
      }
    : null;

  return (
    <div className="verify-page">
      <div className="verify-shell">
        {/* ============================================================
            TOP — Trust Banner
            ============================================================ */}
        <div className={`verify-banner ${isValid ? 'is-valid' : 'is-invalid'}`}>
          <div className="verify-banner-top">
            <div className="verify-banner-icon">{isValid ? '✓' : '✕'}</div>

            <div className="verify-banner-body">
              <span className="verify-banner-eyebrow">
                {isValid ? 'Certificate Verified' : 'Verification Failed'}
              </span>
              <h1 className="verify-banner-title">
                {isValid
                  ? 'This Certificate is Authentic'
                  : 'This Certificate is Not Valid'}
              </h1>
              <p className="verify-banner-sub">
                {isValid
                  ? 'The certificate below is officially issued by AV Tech Baramati Private Limited.'
                  : result?.message || 'The certificate could not be verified.'}
              </p>
            </div>
          </div>

          {isValid && (
            <div className="verify-banner-badge">
              <span className="verify-banner-badge-dot" />
              Verified
            </div>
          )}
        </div>

        {/* ============================================================
            MIDDLE — Metadata grid
            ============================================================ */}
        {d && (
          <div className="verify-meta-grid">
            <MetaItem label="Certificate ID" value={d.certificateId} mono />
            <MetaItem label="Issued To" value={d.recipientName} />
            <MetaItem
              label="Participant Type"
              value={
                isProfessional
                  ? '💼 Non-Student / Professional'
                  : '🎓 Student'
              }
            />
            {d.universityName && (
              <MetaItem
                label={isProfessional ? 'Organisation' : 'University'}
                value={d.universityName}
              />
            )}
            <MetaItem label="Field" value={d.fieldName || '—'} />
            <MetaItem
              label="Duration"
              value={`${fmtDate(d.startDate)} → ${fmtDate(d.endDate)}`}
            />
            <MetaItem label="Issued On" value={fmtLongDate(d.issueDate)} />
            <MetaItem label="Status" value={d.status || 'active'} />
          </div>
        )}

        {/* ============================================================
            BOTTOM — Certificate preview (auto-scaled to fit)
            ============================================================ */}
        {isValid && certForPreview && (
          <div className="verify-cert-wrapper">
            <div className="verify-cert-header">
              <div>
                <span className="verify-cert-header-eyebrow">
                  Certificate Preview
                </span>
                <h2 className="verify-cert-header-title">
                  Official Document
                </h2>
              </div>
            </div>

            <div className="verify-cert-frame">
              <div
                ref={scalerRef}
                className="verify-cert-scaler"
                style={{
                  height: `${CERT_NATIVE_HEIGHT_PX * certScale}px`,
                }}
              >
                <div
                  className="verify-cert-inner"
                  style={{
                    width: `${CERT_NATIVE_WIDTH_PX}px`,
                    height: `${CERT_NATIVE_HEIGHT_PX}px`,
                    transform: `scale(${certScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <CertificatePreview
                    data={certForPreview}
                    mode="form"
                    printId="verify-printable-certificate"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================
            FOOTER
            ============================================================ */}
        <div className="verify-footer">
          <div className="verify-footer-brand">
            <span className="verify-footer-logo">AV</span>
            <div>
              <div className="verify-footer-name">
                AV Tech Baramati Private Limited
              </div>
              <div className="verify-footer-tag">
                Empowering Skills · Building Futures
              </div>
            </div>
          </div>
          <div className="verify-footer-contact">
            <span>www.avtechbmt.cloud</span>
            <span className="verify-footer-dot">•</span>
            <span>avtech.baramati@gmail.com</span>
            <span className="verify-footer-dot">•</span>
            <span>Baramati, Maharashtra, India</span>
          </div>
        </div>
      </div>
    </div>
  );

  /* ================================================================
     Helpers
     ================================================================ */
  function fmtDate(v) {
    if (!v) return '—';
    const dt = new Date(v);
    if (isNaN(dt)) return '—';
    return dt.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  function fmtLongDate(v) {
    if (!v) return '—';
    const dt = new Date(v);
    if (isNaN(dt)) return '—';
    return dt.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
}

/* ==========================================================================
   Small helper component for metadata rows
   ========================================================================== */
function MetaItem({ label, value, mono = false }) {
  return (
    <div className="verify-meta-item">
      <span className="verify-meta-label">{label}</span>
      <span className={`verify-meta-value ${mono ? 'is-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}